import React, { useState, useEffect } from "react";
import {
  CircularProgressbar,
  CircularProgressbarWithChildren,
  buildStyles,
} from "react-circular-progressbar";
import PropTypes from "prop-types";
import { SIGNOL_COLOUR_NAMES } from "@signolteam/shared/colours";
import Icon from "./Icon.jsx";
import AppUtils from "@src/utils/appUtils";
import { selectOptionsAllColourHexCodes } from "@src/stories/utils/control-helpers";

const Separator = (props) => {
  return (
    <div
      style={{
        position: "absolute",
        height: "100%",
        backgroundColor: `${props.background}`,
        transform: `rotate(${props.turns}turn)`,
      }}
    >
      <div style={props.style} />
    </div>
  );
};

const RadialSeparators = (props) => {
  const turns = 1 / props.count;
  return [...Array(props.count).keys()].map((index) => (
    <Separator key={index} turns={index * turns} style={props.style} />
  ));
};

const SectionedProgressBarCircular = ({
  size = "big",
  bgColor,
  targetAchieved,
  successValue,
  goalValue,
  empty = false,
  lineColor,
  goalColor = "black",
  successColor,
  parentColor,
  colorName,
  behaviourAcronym,
}) => {
  const [animate, setAnimate] = useState(true);
  const circleSize = size === "big" ? 250 : 150;
  const textSize = size === "big" ? "text-lg" : "text-xs";
  const numberSize = size === "big" ? "text-6xl" : "text-4xl";
  const numberLeading = size === "big" ? "leading-20" : "leading-8";
  const centerSize = size === "big" ? "140px" : "80px";
  const numberOfSegments = empty ? 7 : goalValue;
  const numberOfFilledSegments = empty ? 0 : successValue;
  const filledSegments =
    numberOfFilledSegments > numberOfSegments
      ? numberOfSegments
      : numberOfFilledSegments; // if the success number is higher than the goal just fill all the segments in dont draw extra on top
  const sections = [];
  const animationTotalDuration = 1.5;
  const stepAnimationDuration = 0.25;
  const stepAnimationDelayIncrement =
    numberOfFilledSegments > 0
      ? (animationTotalDuration - stepAnimationDuration) /
        numberOfFilledSegments
      : 0;

  useEffect(() => {
    let isMounted = true;

    setAnimate(true); // reset animation if behaviour changes
    const timer = setTimeout(() => {
      if (isMounted) {
        setAnimate(false); // run the animation
      }
    }, 100);

    return () => {
      isMounted = false;
      clearTimeout(timer); // cleanup the timer
    };
  }, [behaviourAcronym]);

  for (let s = 0; s < filledSegments; s++) {
    sections.push(
      <div
        key={`${behaviourAcronym}_segment_${s}`}
        className={`absolute fade-in${!animate ? " in" : ""}`}
        style={{
          width: circleSize,
          height: circleSize,
          zIndex: s + 1,
          transitionDuration: `${stepAnimationDuration}s`,
          transitionDelay: `${stepAnimationDelayIncrement * (s + 1)}s`,
        }}
      >
        <CircularProgressbar
          strokeWidth={10}
          value={((s + 1) * 100) / numberOfSegments}
          background
          backgroundPadding={4}
          styles={buildStyles({
            backgroundColor: "transparent",
            trailColor: "transparent",
            pathColor: lineColor,
          })}
        />
      </div>,
    );
  }

  return (
    <div
      className="mx-auto text-center relative text-neutral-300"
      style={{ width: circleSize, height: circleSize }}
    >
      <div
        className="absolute"
        style={{
          width: circleSize,
          height: circleSize,
          zIndex: numberOfSegments + 2,
        }}
      >
        <CircularProgressbarWithChildren
          strokeWidth={0}
          value={(numberOfFilledSegments * 100) / numberOfSegments}
          background
          backgroundPadding={4}
          styles={buildStyles({
            backgroundColor: "transparent",
            trailColor: "transparent",
            pathColor: "transparent",
          })}
        >
          <RadialSeparators
            count={numberOfSegments}
            style={{
              background: !empty && targetAchieved ? bgColor : "#fff",
              width: "2px",
              // This needs to be equal to props.strokeWidth
              height: `20%`,
            }}
          />
        </CircularProgressbarWithChildren>
      </div>
      {sections}
      <CircularProgressbarWithChildren
        strokeWidth={10}
        value={0}
        background
        backgroundPadding={4}
        styles={buildStyles({
          backgroundColor: targetAchieved ? bgColor : "transparent",
          trailColor: parentColor,
          pathColor: lineColor,
        })}
      >
        <div
          className={`absolute inset-auto text-${!empty && targetAchieved ? "white" : "black"}`}
        >
          {!empty && (
            <div
              id={"number-in-circular-ring"}
              className={`${textSize} font-bold text-center rounded-full p-3`}
              style={{
                backgroundColor: targetAchieved ? successColor : parentColor,
                width: centerSize,
                height: centerSize,
              }}
            >
              <div style={{ color: goalColor }}>Goal:</div>
              <div
                className={`${numberSize} ${numberLeading}`}
                style={{ color: goalColor }}
              >
                {goalValue}
              </div>
              {/* <div className="lowercase">{eventName}</div> */}
            </div>
          )}
          {empty && (
            <div
              className={`${textSize} font-bold text-center rounded-full p-3`}
              style={{
                backgroundColor: parentColor,
                width: centerSize,
                height: centerSize,
              }}
            >
              <Icon
                name="timer"
                textColor={`${colorName}-400`}
                backgroundColor={"transparent"}
                size="full"
                padding={false}
              />
            </div>
          )}
        </div>
      </CircularProgressbarWithChildren>
    </div>
  );
};

export const sectionedProgressBarCircularControls = {
  size: {
    propType: PropTypes.string,
    description: "Size option",
    controlType: "select",
    options: ["medium", "big"],
  },
  bgColor: {
    propType: PropTypes.string,
    description:
      "Background colour around the segment and making the gap between segments",
    controlType: "select",
    options: selectOptionsAllColourHexCodes([300]),
  },
  lineColor: {
    propType: PropTypes.string,
    description: "Color for segments that represent a success",
    controlType: "select",
    options: selectOptionsAllColourHexCodes([500]),
  },
  goalColor: {
    propType: PropTypes.string,
    description: "Colour for the word 'Goal'",
    controlType: "select",
    options: selectOptionsAllColourHexCodes([700]),
  },
  successColor: {
    propType: PropTypes.string,
    description: "Background colour",
    controlType: "select",
    options: selectOptionsAllColourHexCodes([400]),
  },
  parentColor: {
    propType: PropTypes.string,
    description: "Background colour",
    controlType: "select",
    options: {
      transparent: "transparent",
      ...selectOptionsAllColourHexCodes([200]),
    },
  },
  colorName: {
    propType: PropTypes.string,
    description: "Colour name",
    controlType: "select",
    options: SIGNOL_COLOUR_NAMES,
  },
  targetAchieved: {
    propType: PropTypes.bool,
    description:
      "Whether the goal is achieved or not - will drive whether the partial segments show or not",
    controlType: "boolean",
    defaultValue: false,
  },
  empty: {
    propType: PropTypes.bool,
    description: "Empty state",
    controlType: "boolean",
    defaultValue: false,
  },
  eventName: {
    propType: PropTypes.string,
    description: "eventName",
    controlType: "text",
  },
  behaviourAcronym: {
    propType: PropTypes.string,
    description: "Behaviour acronym",
    controlType: "text",
  },
  goalValue: {
    propType: PropTypes.number,
    description: "Number of goals set for the period",
    controlType: "number",
  },
  successValue: {
    propType: PropTypes.number,
    description: "Number of goals achieved",
    controlType: "number",
  },
};

SectionedProgressBarCircular.PropTypes = AppUtils.getPropTypeFromControls(
  sectionedProgressBarCircularControls,
);

export default SectionedProgressBarCircular;
